import React, { PropsWithChildren } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Box, Icon, Text } from '@sparknz/spark-pattern-library';
import { LinkRegion, trackCustomLink } from '@sparknz/tracking';

import { useProductDetails } from 'journey/productDetails/provider';
import { ProductDetailsActionTypes } from 'journey/productDetails/reducer/productDetails.reducer';
import {
  IfpEligibilityCheckSteps,
  Section,
} from 'journey/productDetails/typings';

const BreadcrumbText = styled(Text)`
  white-space: nowrap;
`;

const BreadcrumbAnchor = styled.a`
  text-decoration: none;
  cursor: pointer;
`;

const ChevronIcon = styled(Icon)`
  ${({ theme: { spacing } }) => css`
    width: 16px;
    height: 16px;
    margin: 0px ${spacing.s1};
  `}
`;

interface PreviousBreadcrumbTextLinkProps {
  activeStepId: IfpEligibilityCheckSteps;
  sectionName: string;
}

function PreviousBreadcrumbTextLink({
  children,
  activeStepId,
  sectionName,
}: PropsWithChildren<PreviousBreadcrumbTextLinkProps>) {
  const { palette } = useTheme();
  const { dispatch } = useProductDetails();

  const handleClick = () => {
    trackCustomLink(sectionName);
    dispatch({
      type: ProductDetailsActionTypes.SetIfpEligibility,
      ifpEligibility: {
        activeStep: activeStepId,
      },
    });
  };

  return (
    <BreadcrumbAnchor onClick={handleClick}>
      <BreadcrumbText
        variant="footnoteMedium"
        color={palette.baseColors.greymouth}
      >
        {children}
      </BreadcrumbText>
    </BreadcrumbAnchor>
  );
}

interface BreadcrumbProps {
  activeIndex: number;
  index: number;
  isLastSection: boolean;
  section: Section;
}

function Breadcrumb({
  activeIndex,
  index,
  isLastSection,
  section,
}: BreadcrumbProps) {
  const {
    palette: {
      baseColors: { moeraki, poroporo },
    },
  } = useTheme();
  const isPreviousSection = index < activeIndex;
  const isActiveSection = index === activeIndex;
  const color = isActiveSection ? poroporo : moeraki;
  return (
    <Box flexDirection="row" alignItems="center">
      {isPreviousSection ? (
        <LinkRegion regionId="productDetails.plan.breadcrumbs">
          <PreviousBreadcrumbTextLink
            activeStepId={section.value}
            sectionName={section.name}
          >
            {section.name}
          </PreviousBreadcrumbTextLink>
        </LinkRegion>
      ) : (
        <LinkRegion regionId="productDetails.plan.breadcrumbs">
          <BreadcrumbText variant="footnoteMedium" color={color}>
            {section.name}
          </BreadcrumbText>
        </LinkRegion>
      )}
      {isLastSection ? (
        <ChevronIcon name="chevron_right_small" fill={moeraki} />
      ) : null}
    </Box>
  );
}

const BreadCrumbs = () => {
  const { ifpEligibility } = useProductDetails();
  return (
    <Box
      px={['s3', 's4', 's6']}
      pb={['s3', 's4', 's4']}
      pt="s1"
      flexDirection="row"
      alignItems="center"
    >
      {ifpEligibility.sections.map((section, index) => {
        const activeIndex = ifpEligibility.sections.find(
          (item) => item.value === ifpEligibility.activeStep,
        )?.id;
        return (
          <Breadcrumb
            activeIndex={activeIndex}
            index={index}
            section={section}
            key={section.id}
            isLastSection={index < ifpEligibility.sections.length - 1}
          />
        );
      })}
    </Box>
  );
};

export default BreadCrumbs;
